import { CrudService } from '@/shared/services/crud.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AppState } from 'src/app/viewbid/state/app-state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { VbSnackbarService } from '@viewbid/ui';
import { Observable, catchError, throwError } from 'rxjs';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { User } from 'viewbid-shared-lib';
import {
    AuctionCatalogsService,
    CreateAdminInvoice201Response,
    GetLotById200Response,
} from '@viewbid/ts-angular-client';

/** Link to the lot or invoice the thread is about, resolved on open. */
interface ThreadContextLink {
    label: string;
    route: string[];
}

/**
 * Message-thread dialog — composes the vb-dialog shell (profile redesign),
 * replacing the legacy ngx-bootstrap modal. The parent owns [open] and the
 * thread identity inputs; every open resets the compose state and refetches
 * the thread.
 */
@Component({
    selector: 'vb-message-thread',
    templateUrl: './message-thread.component.html',
    styleUrls: ['./message-thread.component.scss'],
})
export class MessageThreadComponent implements OnChanges {
    @Input() open: boolean = false;
    /** Raw topic key — sent back with new replies. */
    @Input() topic?: string;
    /** Human-readable topic — the dialog title. */
    @Input() topicLabel?: string;
    @Input() topicId?: string;
    @Input() accountMappingsId?: string;
    /** Context ids carried on the discussion row (null on threads created
     *  before context ids were stored) — resolved into a link on open. */
    @Input() invoiceId?: string;
    @Input() auctionId?: string;
    @Input() lotId?: string;

    /** Dismissed (backdrop / Escape / ✕ / Cancel). The parent closes the
     *  dialog and reloads the list so read-state changes surface. */
    @Output() closed: EventEmitter<void> = new EventEmitter();
    /** In-thread "Delete conversation" — emits the topicId; the parent runs
     *  the same confirm flow as the table's row kebab. */
    @Output() deleteConversation: EventEmitter<string> = new EventEmitter();

    public user: User | null = null;
    public user$: Observable<User | null>;
    public originalId: string = '';
    /** "Lot 12 · Auction name" / "Invoice #123" link above the ledger;
     *  null while resolving, on lookup failure, or without context ids. */
    public contextLink: ThreadContextLink | null = null;
    public comments?: Array<any> = [];
    public isLoading: boolean = true;
    public isMessageSend: boolean = false;
    public isMessageFormError: boolean = false;
    public messengerForm = new FormGroup({
        message: new FormControl('', [Validators.required]),
    });

    constructor(
        public translate: TranslateService,
        public crudService: CrudService,
        public snackbarService: VbSnackbarService,
        private store: Store<AppState>,
        private auctionCatalogsService: AuctionCatalogsService,
    ) {
        this.user$ = this.store.select<User | null>(selectUser);
        this.user$.subscribe((user: User | null) => {
            this.user = user;
        });
    }

    /** Each open is a fresh thread view: clear the stale conversation and
     *  compose state before fetching, so the previous topic can't flash. */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open'] && this.open && this.topicId) {
            this.comments = [];
            this.isLoading = true;
            this.isMessageSend = false;
            this.isMessageFormError = false;
            this.messengerForm.controls['message'].setValue('');
            this.loadPage();
            this.loadContextLink();
        }
    }

    /** Resolves the thread's lot / invoice ids into a display link — lot
     *  number + auction name, or invoice number. Lookup failures just leave
     *  the link out; the thread itself is unaffected. */
    private loadContextLink(): void {
        this.contextLink = null;
        const requestedTopicId: string | undefined = this.topicId;

        if (this.lotId && this.auctionId) {
            const auctionId: string = this.auctionId;
            const lotId: string = this.lotId;
            this.auctionCatalogsService.getLotById(lotId).subscribe({
                next: (response: GetLotById200Response) => {
                    if (!this.open || this.topicId !== requestedTopicId) {
                        return;
                    }
                    this.contextLink = {
                        label: `Lot ${response.data.lotNumber} · ${response.data.auctionName}`,
                        route: ['/auctions', auctionId, 'lot', lotId],
                    };
                },
                error: () => {
                    /* closed/archived lot — no link */
                },
            });
            return;
        }

        if (this.invoiceId) {
            const invoiceId: string = this.invoiceId;
            this.auctionCatalogsService
                .getUserInvoiceById(invoiceId)
                .subscribe({
                    next: (response: CreateAdminInvoice201Response) => {
                        if (!this.open || this.topicId !== requestedTopicId) {
                            return;
                        }
                        const invoiceNumber: number | undefined =
                            response.data.invoice?.number;
                        if (invoiceNumber == null) return;
                        this.contextLink = {
                            label: `Invoice #${invoiceNumber}`,
                            route: [
                                '/profiles/my-profile/view-invoice',
                                invoiceId,
                            ],
                        };
                    },
                    error: () => {
                        /* inaccessible invoice — no link */
                    },
                });
        }
    }

    /** Compose length for the "n / 1000" counter. */
    get messageLength(): number {
        return this.messengerForm.controls['message'].value?.length ?? 0;
    }

    loadPage(): void {
        this.crudService
            .fetchData(
                `/api/notifications/user-messages/topic-id/${this.topicId}?page=1&size=100`,
            )
            .pipe(
                catchError((HttpError: HttpErrorResponse): any => {
                    this.isLoading = false;
                    this.crudError(HttpError);
                    return throwError(() => HttpError);
                }),
            )
            .subscribe((comments: any) => {
                this.comments = [...comments.data];
                this.originalId = this.comments[0]?.accountMappingsId ?? null;
                this.isLoading = false;
                this.isMessageSend = false;
            });
    }

    onSubmit(): void {
        this.isMessageSend = true;
        const message: any = this.messengerForm.controls['message'];
        if (this.messengerForm.status === 'VALID') {
            this.isMessageFormError = false;
            this.submitForm(message.value ?? '');
        } else {
            this.isMessageSend = false;
            this.isMessageFormError = true;
        }
    }

    onMessageInput(): void {
        if (this.messengerForm.status === 'VALID') {
            this.isMessageFormError = false;
        }
    }

    submitForm(comments: string): void {
        const username: string | undefined = this.user?.['username'];
        const body: any = {
            comments: comments,
            topic: this.topic,
            topicId: this.topicId,
            username: username,
            status: 'Unread',
            accountMappingsId: this.accountMappingsId,
        };
        this.crudService
            .addData('/api/notifications/user-messages', body)
            .pipe(
                catchError((HttpError: HttpErrorResponse): any => {
                    this.crudError(HttpError);
                    return throwError(() => HttpError);
                }),
            )
            .subscribe(() => {
                this.crudSuccess();
                this.messengerForm.controls['message'].setValue('');
                this.loadPage();
            });
    }

    crudError(HttpError: HttpErrorResponse): void {
        this.isMessageSend = false;
        this.snackbarService.dismissAll();
        this.snackbarService.error(
            HttpError.error.message
                ? HttpError.error.message
                : HttpError.message,
        );
    }

    crudSuccess(): void {
        this.snackbarService.dismissAll();
        this.snackbarService.success(
            this.translate.instant('Chatbox.messageSentToastr'),
        );
    }

    onClose(): void {
        this.closed.emit();
    }

    onDeleteConversation(): void {
        if (this.topicId) {
            this.deleteConversation.emit(this.topicId);
        }
    }
}
